"use client";

import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";

import { ProductImage } from "@/components/catalog/product-image";
import { subtleTween, usePressedInteraction } from "@/components/ui/motion-primitives";

export interface ProductGalleryProps {
  readonly images: readonly {
    readonly alt: string;
    readonly quality?: number;
    readonly unoptimized?: boolean;
    readonly url: string | null;
  }[];
  readonly productName: string;
}

export function ProductGallery({ images, productName }: Readonly<ProductGalleryProps>) {
  const validImages = useMemo(() => {
    const urls = new Set<string>();

    return images.filter((image) => {
      const key = image.url ?? "fallback";

      if (urls.has(key)) {
        return false;
      }

      urls.add(key);
      return true;
    });
  }, [images]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const thumbnailInteraction = usePressedInteraction();
  const selectedImage = validImages[selectedIndex] ?? validImages[0] ?? {
    alt: productName,
    url: null,
  };

  return (
    <section className="mt-4 rounded-xl border border-outline-variant bg-surface p-3 shadow-sm">
      <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-white p-0">
        <AnimatePresence mode="wait">
          <motion.div
            animate={{ opacity: 1 }}
            className="h-full w-full"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            key={`${selectedImage.url ?? "fallback"}-${selectedIndex}`}
            transition={subtleTween}
          >
            <ProductImage
              alt={selectedImage.alt}
              className="h-full w-full object-contain"
              imageUrl={selectedImage.url}
              priority={selectedIndex === 0}
              quality={selectedImage.quality ?? 88}
              sizes="(max-width: 767px) min(calc(100vw - 32px), 416px), (max-width: 1279px) calc((100vw - 64px) / 2), 544px"
              unoptimized={selectedImage.unoptimized}
            />
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2">
        {validImages.map((image, index) => (
          <motion.button
            aria-label={`Ver imagen ${index + 1}`}
            className={`flex aspect-square items-center justify-center overflow-hidden rounded-lg border bg-white p-[5px] ${
              selectedIndex === index ? "border-primary" : "border-outline-variant"
            }`}
            key={`${image.url ?? "fallback"}-${index}`}
            layout="position"
            onClick={() => setSelectedIndex(index)}
            type="button"
            {...thumbnailInteraction}
          >
            <ProductImage
              alt={image.alt}
              imageUrl={image.url}
              loading="lazy"
              quality={75}
              sizes="96px"
            />
          </motion.button>
        ))}
      </div>
    </section>
  );
}
